import * as React from "react";
import { Modal, Table, Tag, Typography, Pagination, Spin, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import useSWR from "swr";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { getRewardIssuesFailedApi, reissueRewardApi } from "src/api/campaign/campaignApi";
import type { RewardIssueFailed } from "src/types/campaign";
import { snackbarStore } from "src/store/snackbarStore";

type Props = {
  open: boolean;
  campaignId: number;
  onClose: () => void;
};

const PAGE_SIZE = 20;

const issueStatusColor: Record<string, string> = {
  FAILED: "error",
  PENDING: "warning",
  COMPLETED: "success",
  REISSUED: "geekblue",
};

const triggerMethodLabel: Record<string, string> = {
  MANUAL: "수동 발급",
  CLAIM: "직접 수령",
  AUTO: "자동 발급",
  EVENT_ATTENDANCE: "출석체크",
  EVENT_RANDOM: "확률 기반",
};

export default function RewardIssueFailedModal({ open, campaignId, onClose }: Props) {
  const [page, setPage] = React.useState(1);
  const [reissuingIds, setReissuingIds] = React.useState<Set<number>>(new Set());

  const swrKey = React.useMemo(
    () => (open ? (["reward-issuances/failed", campaignId, page] as const) : null),
    [open, campaignId, page]
  );

  const { data, isLoading, mutate } = useSWR(
    swrKey,
    async () =>
      getRewardIssuesFailedApi({
        campaignId,
        page: Math.max(0, page - 1),
        size: PAGE_SIZE,
      }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  React.useEffect(() => {
    if (open) setPage(1);
  }, [open]);

  const issues = data?.data?.content ?? [];
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const handleReissue = async (record: RewardIssueFailed) => {
    const result = await Swal.fire({
      title: "재발급 확인",
      text: `이슈 ID ${record.issueId} (${record.username})를 재발급하시겠습니까?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "재발급",
      cancelButtonText: "취소",
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = "3000";
      },
    });
    if (!result.isConfirmed) return;

    setReissuingIds((prev) => new Set(prev).add(record.issueId));
    try {
      const res = await reissueRewardApi(record.issueId);
      // 성공 snackbar는 axiosInstance 인터셉터가 처리
      // 원본 캠페인 규칙이 승계되지 않은 경우 경고를 별도로 안내
      const data = res.data;
      if (res.statusCode === 1 && data && data.campaignRuleLinked === false) {
        snackbarStore.getState().addSnackbar({
          message: data.warning || "원본 캠페인 규칙이 연결되지 않은 상태로 재발급되었습니다.",
          severity: "warning",
        });
      }
      await mutate();
    } catch {
      // 에러 snackbar는 axiosInstance 인터셉터가 처리
    } finally {
      setReissuingIds((prev) => {
        const next = new Set(prev);
        next.delete(record.issueId);
        return next;
      });
    }
  };

  const columns: ColumnsType<RewardIssueFailed> = [
    { title: "이슈 ID", dataIndex: "issueId", width: 80 },
    { title: "사용자 ID", dataIndex: "userId", width: 90 },
    { title: "이름", dataIndex: "username", width: 100 },
    { title: "이메일", dataIndex: "email", width: 180 },
    { title: "리워드명", dataIndex: "rewardName", width: 140 },
    {
      title: "트리거",
      dataIndex: "triggerMethod",
      width: 110,
      render: (value) => <Tag>{triggerMethodLabel[value] || value}</Tag>,
    },
    {
      title: "상태",
      dataIndex: "issueStatus",
      width: 90,
      render: (value) => <Tag color={issueStatusColor[value] || "default"}>{value}</Tag>,
    },
    {
      title: "오류 내용",
      dataIndex: "errorDescription",
      width: 180,
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "발급일시",
      dataIndex: "issuedAt",
      width: 160,
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-"),
    },
    {
      title: "재발급",
      key: "reissue",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          danger
          loading={reissuingIds.has(record.issueId)}
          onClick={() => handleReissue(record)}
        >
          재발급
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title={`실패/미발급 리워드 목록 (캠페인 ID: ${campaignId})`}
      open={open}
      onCancel={onClose}
      width={1200}
      footer={null}
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <Spin />
        </div>
      ) : (
        <>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            총 {totalElements.toLocaleString()}건
          </Typography.Text>

          <Table
            columns={columns}
            dataSource={issues}
            rowKey={(record) => String(record.issueId)}
            pagination={false}
            loading={isLoading}
            size="small"
            locale={{ emptyText: "실패/미발급 내역이 없습니다." }}
            scroll={{ x: 1000 }}
          />

          {totalElements > PAGE_SIZE && (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 16 }}>
              <Pagination
                current={page}
                total={totalElements}
                pageSize={PAGE_SIZE}
                onChange={setPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
